<template>
  <div class="plan-information">
    <action-dispatcher
      action="product/getProduct"
      :parameters="$route.params.id"
    >
      <div class="d-flex align-items-center justify-content-between mb-7">
        <h5 class="text-semibold">
          {{ editing ? "Editar plano" : "Novo plano" }}
        </h5>
        <div class="d-flex gap-3">
          <snow-button
            size="sm"
            variant="gray"
            text="Cancelar"
            @click="$router.push({ name: 'product-plans' })"
          />
          <snow-button
            size="sm"
            @click="submit"
            :disabled="!isValid"
            text="Salvar"
            :loading="loadingSubmit"
          />
        </div>
      </div>

      <div class="d-flex justify-content-center my-9" v-if="loading">
        <b-spinner variant="primary" />
      </div>

      <div class="d-flex flex-column gap-4" v-else>
        <snow-input
          v-model="form.name"
          id="name"
          required
          label="Nome do plano"
          description="Este nome será exibido para os compradores."
          :errors="errors.name"
        />
        <snow-input
          v-model="form.description"
          id="description"
          required
          textarea
          :limit="980"
          label="Descrição no checkout"
          :errors="errors.description"
        />

        <h5 class="text-semibold">Origem de vendas</h5>

        <snow-input
          v-model="form.sck"
          id="sck"
          label="SCK"
          info="Esse parâmetro te ajudará a identificar a origem da compra."
        />

        <h5 class="text-semibold">Precificação</h5>

        <snow-input
          v-model="form.price"
          money
          :moneyConfig="moneyConfig"
          id="price"
          label="Valor em Real Brasileiro (R$)"
          :errors="errors.price"
          required
          info="Defina quanto custará o seu produto. Certifique-se de que o valor não seja menor que R$ 4,00."
          :min="4"
        />
        <snow-input
          v-model="form.price_usd"
          money
          id="price"
          label="Valor em Dólar Americano (US$)"
          :min="4"
          :info="getInfo('USD')"
          :disabled="!showForeign"
          :errors="errors.price_usd"
          :moneyConfig="{ ...moneyConfig, currency: '$' }"
        />
        <snow-input
          v-model="form.price_eur"
          money
          id="price"
          label="Valor em Euro (€)"
          :min="4"
          :info="getInfo('EUR')"
          :disabled="!showForeign"
          :errors="errors.price_eur"
          :moneyConfig="{ ...moneyConfig, currency: '€' }"
        />

        <snow-select
          v-model="form.periodicity"
          id="frequency"
          class="mt-6"
          required
          :disabled="!!form.id"
          label="Periodicidade"
          :errors="errors.periodicity"
          :options="frequencyOptions"
        />
        <h5 class="text-dark-40">
          Escolha quando deverá ocorrer a renovação da assinatura
        </h5>

        <action-dispatcher
          v-if="form.price && frequencyMonths"
          action="sale/getInstallments"
          :parameters="installmentsParams"
          :key="`${frequencyMonths}${form.price}`"
        >
          <snow-select
            v-model="form.installments"
            id="installment"
            select
            required
            label="Parcelamento"
            :errors="errors.installments"
            :options="installmentOptions"
          />
        </action-dispatcher>

        <snow-input
          v-model="form.test_days"
          id="test_days"
          mask="###"
          :disabled="isTwoCards"
          label="Dias de teste grátis"
          :errors="errors.test_days"
        />

        <h5 v-if="isTwoCards" class="text-danger">
          Opção desabilitada para assinaturas que aceitem pagamento com dois
          cartões.
        </h5>
        <h5 v-else class="text-dark-40">
          Defina quantos dias de teste grátis o cliente terá antes da primeira
          cobrança. <br />
          Transações de período de teste terão cobrança automática de R$ 1,00.
          Caso esteja tudo certo com a transação, o estorno é feito
          automaticamente, mantendo o período de teste estipulado.
        </h5>

        <h5 class="text-semibold mt-4">
          Novo preço depois do primeiro pagamento
        </h5>
        <snow-switch v-model="promotionalPrice" label="Definir novo preço " />

        <snow-input
          v-if="promotionalPrice"
          v-model="form.next_cycle_price"
          id="next_cycle_price"
          required
          money
          :moneyConfig="{ prefix: 'R$ ' }"
          label="Novo preço"
          :errors="errors.next_cycle_price"
          info="Defina qual será o novo valor a ser cobrado no seu plano, certificando-se que não seja um valor menor que R$4,00."
        />

        <snow-radio
          v-model="form.billing"
          id="billing"
          label="Forma de cobrança *"
          :errors="errors.billing"
          :options="billingOptions"
          vertical
        />

        <snow-input
          v-if="form.billing === 2"
          v-model="form.num_max_payments"
          id="num_max_payments"
          mask="#######"
          required
          label="Número máximo de recorrências"
          :errors="errors.name"
          :min="1"
        />
      </div>
    </action-dispatcher>
  </div>
</template>

<script>
import { clearObject, formatMoney, formFields } from "@/functions/helpers";
import get from "lodash/get";
import { mapState } from "vuex";

export default {
  data() {
    return {
      status: false,
      loading: false,
      loadingSubmit: false,
      promotionalPrice: false,
      ...formFields(
        [
          "id",
          "name",
          "description",
          "price",
          "price_usd",
          "price_eur",
          "periodicity",
          "installments",
          "testing",
          "test_days",
          "next_cycle_price",
          "billing",
          "num_max_payments",
          "payment_form",
          "status",
          "sck"
        ],
        {
          test_days: 0
        }
      ),
      billingOptions: [
        { id: 1, name: "Até o cliente cancelar" },
        { id: 2, name: "Definir um número máximo" }
      ],
      moneyConfig: {
        decimal: ",",
        thousands: ".",
        precision: 2,
        prefix: "",
        masked: false
      }
    };
  },
  watch: {
    promotionalPrice(v) {
      if (!v) this.form.next_cycle_price = "";
    },
    "form.billing"(v) {
      if (v === 1) {
        this.form.num_max_payments = 0;
      }
    }
  },
  computed: {
    ...mapState("product", ["product", "frequencyOptions"]),
    ...mapState("sale", ["installments"]),
    ...mapState("plan", ["plan"]),
    editing() {
      return Object.keys(this.$route.params).includes("plan_id");
    },
    isTwoCards() {
      return !!get(this.product, "payment_methods", []).find(
        i => i?.code === "two_credit_card"
      );
    },
    installmentsParams() {
      return clearObject({
        installments: this.frequencyMonths,
        product_id: this.$route.params.id,
        value: this.form.price
      });
    },
    isValid() {
      return (
        this.form.name &&
        this.form.description &&
        this.form.description.length <= 980 &&
        this.form.price &&
        this.form.price >= 4 &&
        (!parseFloat(this.form.price_eur) || this.form.price_eur >= 4) &&
        (!parseFloat(this.form.price_usd) || this.form.price_usd >= 4) &&
        this.form.periodicity &&
        this.form.billing &&
        this.form.installments &&
        (this.form.billing === 1 || Number(this.form.num_max_payments)) &&
        (this.form.next_cycle_price >= 4 || !this.promotionalPrice)
      );
    },
    frequencyMonths() {
      if (!this.form.periodicity) {
        return 0;
      }
      if (["weekly", "monthly"].includes(this.form.periodicity)) {
        return 1;
      }
      return this.frequencyOptions.find(
        item => item.id === this.form.periodicity
      ).months;
    },
    installmentOptions() {
      const opts = [];
      for (let id in this.installments) {
        opts.push({
          id: parseInt(id),
          name: `Até ${id} parcela${id - 1 ? "s" : ""} de R$ ${formatMoney(
            this.installments[id]
          )}`
        });
      }
      return opts;
    },
    showForeign() {
      return this.product.payment_methods?.some(({ id }) => id === 1);
    }
  },
  methods: {
    getInfo(currency) {
      if (this.showForeign) return "";
      const currencyName = {
        USD: "dólar americano",
        EUR: "euro"
      };
      return `Para habilitar configuração de preço em ${currencyName[currency]} seu checkout precisa aceitar Cartão de Crédito. Atualize a configuração de "Opções de Pagamento" do seu produto em "Configurar Checkout"`;
    },
    submit() {
      this.loadingSubmit = true;

      const data = {
        ...this.form,
        status: this.status || !this.editing ? "active" : "inactive",
        product_id: this.$route.params.id
      };

      data.price_eur = parseFloat(data.price_eur) === 0 ? "" : data.price_eur;
      data.price_usd = parseFloat(data.price_usd) === 0 ? "" : data.price_usd;

      const action = this.editing ? "plan/put" : "plan/post";

      this.$store
        .dispatch(action, data)
        .then(() => {
          this.$message.success("Plano salvo com sucesso");
          this.$router.push({
            name: "product-plans",
            params: { id: this.$route.params.id }
          });
        })
        .catch(error => {
          this.errors = error?.response?.data || {};
          this.$message.error("Erro ao salvar plano");
        })
        .finally(() => (this.loadingSubmit = false));
    }
  },
  mounted() {
    if (this.$route.params.plan_id) {
      this.loading = true;
      this.$store
        .dispatch("plan/getPlan", this.$route.params.plan_id)
        .then(data => {
          this.promotionalPrice = !!parseInt(data.next_cycle_price);
          this.status = data.status === "active";
          this.form = {
            ...this.form,
            ...data,
            next_cycle_price: data.next_cycle_price || 0
          };
          this.form.billing = data.num_max_payments ? 2 : 1;
        })
        .finally(() => (this.loading = false));
    }
  }
};
</script>
<style scoped lang="scss">
.plan-information {
  background-color: var(--bg-2);
  padding: 24px;
  border-radius: 16px;
}
</style>
